import { ListBulletIcon, PlusCircleIcon, PuzzlePieceIcon } from "@heroicons/react/24/solid";
import { FC, Fragment, HTMLAttributes, useEffect, useState } from "react";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { LocationFormProps } from "data/reduxInterfaces";
import { SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAppDispatch, useAppSelector } from "hooks/hooks";
import { fetchPlaygrounds } from "redux/features/Admin/playground/playgroundSlice";
import { Checkbox, Spinner, Typography } from "@material-tailwind/react";
import { fetchEquipments } from "redux/features/Admin/equipments/equipmentSlice";
import Input from "shared/Input/Input";
import NcInputNumberOne from "components/NcInputNumber/NcInputNumberOne";

interface Playground {
  id: number;
  designation: string;
}

interface Equipment {
  id: number;
  designation: string;
}

const formSchema = z.object({
  equipment: z.array(z.string()).optional(),
  extraEquipment: z
    .array(
      z.object({
        designation: z.string(),
        price: z.number(),
        quantity: z.number(),
      })
    )
    .optional(),
  playground: z.array(z.string()).optional(),
});

const LocationPlayground: FC<LocationFormProps & HTMLAttributes<HTMLDivElement>> = ({
  isLoading,
  handleForm,
  data,
  FormData,
}) => {
  const dispatch = useAppDispatch();

  const playgroundsStates = useAppSelector((state) => state.featurePlayground);
  const equipmentsStates = useAppSelector((state) => state.featureEquipment);

  const {
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
    trigger,
  } = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      playground: data?.playground ?? [],
      equipment: data?.equipment ?? [],
      extraEquipment: data?.extraEquipment ?? [],
    },
    mode: "onSubmit",
  });

  const [dataLoading, setDataLoading] = useState(true);

  const [extraName, setExtraName] = useState<string>("");
  const [extraPrice, setExtraPrice] = useState<number | undefined>();
  const [extraQuantity, setExtraQuantity] = useState<number | undefined>(1);

  const [extraNameErrorMsg, setExtraNameErrorMsg] = useState<string | null>(null);
  const [extraPriceErrorMsg, setExtraPriceErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchEquipments());
    dispatch(fetchPlaygrounds());
  }, []);

  useEffect(() => {
    if (data) {
      setValue("playground", data.playground);
      setValue("equipment", data.equipment);
      setValue("extraEquipment", data.extraEquipment);
    }
  }, [data]);

  useEffect(() => {
    if (playgroundsStates.Playgrounds) {
      setDataLoading(false);
    }
  }, [playgroundsStates.Playgrounds]);

  useEffect(() => {
    if (equipmentsStates.equipments) {
      setDataLoading(false);
    }
  }, [equipmentsStates.equipments]);

  useEffect(() => {
    if (handleForm.type === "chekLocationFormFour") {
      handleSubmit(onSubmit)();
    }
  }, [handleForm]);

  const handleAddExtra = () => {
    if (!extraName) {
      setExtraNameErrorMsg("Ce champ et obligatoire");
    } else {
      setExtraNameErrorMsg(null);
    }

    if (extraPrice === undefined) {
      setExtraPriceErrorMsg("Ce champ et obligatoire");
    } else {
      setExtraPriceErrorMsg(null);
    }

    if (extraName && extraPrice !== undefined && extraQuantity !== undefined) {
      const extraValues = getValues("extraEquipment") || [];
      setValue("extraEquipment", [
        ...extraValues,
        {
          designation: extraName,
          price: extraPrice,
          quantity: extraQuantity,
        },
      ]);
      trigger("extraEquipment");

      setExtraName("");
      setExtraPrice(undefined);
      setExtraQuantity(1);
    }
  };

  const handleSelectEquipment = (equipmentId: string, isChecked: boolean) => {
    const updatedEquipment = isChecked
      ? [...(getValues("equipment") ?? []), equipmentId]
      : getValues("equipment")?.filter((equipment: string) => equipment !== equipmentId);

    setValue("equipment", updatedEquipment);
    trigger("equipment");
  };

  const handleRemoveExtra = (index: number) => {
    const extraEquipment = [...(getValues("extraEquipment") || [])];
    extraEquipment.splice(index, 1);
    setValue("extraEquipment", extraEquipment);
    trigger("extraEquipment");
  };

  const handleSelectPlayground = (playgroundDesignation: string, isChecked: boolean) => {
    const updatedPlaygrounds = isChecked
      ? [...(getValues("playground") ?? []), playgroundDesignation]
      : getValues("playground")?.filter((playground: string) => playground !== playgroundDesignation);

    setValue("playground", updatedPlaygrounds);
    trigger("playground");
  };

  const onSubmit: SubmitHandler<z.infer<typeof formSchema>> = (data) => {
    FormData(data);
  };

  return (
    <Fragment>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex items-center mb-6">
          <ListBulletIcon className="w-6 h-6 text-eventToBee mr-3" />
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Équipements et installations</h3>
        </div>

        <p className="text-gray-600 dark:text-gray-400 mb-8">
          Sélectionnez les équipements inclus gratuitement et ajoutez des options payantes si nécessaire
        </p>

        {/* Équipements événementiels */}
        <div className="mb-8">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
            Équipements événementiels inclus
          </label>
          <div
            className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 ${
              equipmentsStates.equipments.length > 10 ? "xl:grid-cols-4" : ""
            }`}
          >
            {dataLoading ? (
              <div className="flex items-center justify-center col-span-full py-8">
                <Spinner color="blue" className="w-12 h-12" />
              </div>
            ) : (
              equipmentsStates.equipments?.map((equipment: Equipment) => (
                <div key={equipment.id} className="flex items-center">
                  <Checkbox
                    name={String(equipment.id)}
                    label={
                      <Typography className="text-gray-700 dark:text-gray-300 text-sm">
                        {equipment.designation}
                      </Typography>
                    }
                    disabled={isLoading}
                    checked={!!getValues("equipment")?.includes(equipment.designation)}
                    onChange={(e) => handleSelectEquipment(equipment.designation, e.target.checked)}
                    className="text-eventToBee"
                  />
                </div>
              ))
            )}
          </div>
        </div>

        {/* Équipements supplémentaires */}
        <div className="mb-8">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
            Équipements supplémentaires (payants)
          </label>

          {/* Liste des équipements ajoutés */}
          {getValues("extraEquipment") && getValues("extraEquipment")!.length > 0 && (
            <div className="mb-4 bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <div className="space-y-2">
                {getValues("extraEquipment")?.map((extraValues: any, index: number) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-2 bg-white dark:bg-gray-800 rounded border"
                  >
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      <strong>{extraValues.designation}</strong> - Quantité: {extraValues.quantity} -
                      {extraValues.price !== undefined && extraValues.price !== 0 ? (
                        <span className="text-eventToBee"> {extraValues.price} €</span>
                      ) : (
                        <span className="text-green-600"> Gratuit</span>
                      )}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveExtra(index)}
                      className="text-red-500 hover:text-red-700 p-1"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Formulaire d'ajout */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Nom</label>
              <Input
                placeholder="Nom de l'équipement"
                name="extraName"
                value={extraName}
                type="text"
                disabled={isLoading}
                onChange={(e) => setExtraName(e.target.value)}
                className={extraNameErrorMsg ? "border-red-500" : ""}
              />
              {extraNameErrorMsg && <div className="text-xs text-red-500 mt-1">{extraNameErrorMsg}</div>}
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Prix unitaire (€)
              </label>
              <Input
                placeholder="0"
                name="extraPrice"
                value={extraPrice ?? ""}
                type="number"
                disabled={isLoading}
                onChange={(e) => setExtraPrice(Number(e.target.value))}
                className={extraPriceErrorMsg ? "border-red-500" : ""}
              />
              {extraPriceErrorMsg && <div className="text-xs text-red-500 mt-1">{extraPriceErrorMsg}</div>}
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Quantité</label>
              <NcInputNumberOne
                className="p-2"
                defaultValue={extraQuantity}
                min={1}
                onChange={(value) => setExtraQuantity(value)}
              />
            </div>

            <div>
              <ButtonPrimary onClick={handleAddExtra} type="button" disabled={isLoading} className="w-full">
                <PlusCircleIcon className="w-4 h-4 mr-2" />
                Ajouter
              </ButtonPrimary>
            </div>
          </div>
        </div>

        {/* Aires de jeux */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
            Aires de jeux disponibles
          </label>
          <div
            className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 ${
              playgroundsStates.Playgrounds.length > 10 ? "xl:grid-cols-4" : ""
            }`}
          >
            {dataLoading ? (
              <div className="flex items-center justify-center col-span-full py-8">
                <Spinner color="blue" className="w-12 h-12" />
              </div>
            ) : (
              playgroundsStates.Playgrounds?.map((playground: Playground) => (
                <div key={playground.id} className="flex items-center">
                  <Checkbox
                    name={playground.designation}
                    label={
                      <Typography className="text-gray-700 dark:text-gray-300 text-sm">
                        {playground.designation}
                      </Typography>
                    }
                    disabled={isLoading}
                    checked={!!getValues("playground")?.includes(playground.designation)}
                    onChange={(e) => handleSelectPlayground(playground.designation, e.target.checked)}
                    className="text-eventToBee"
                  />
                </div>
              ))
            )}
          </div>
        </div>
      </form>
    </Fragment>
  );
};

export default LocationPlayground;
